import type {Order} from '@wix/ambassador-ep-orders/types'
import {FeeUnit, type Reservation} from '@wix/ambassador-table-reservations-v1-reservation/types'
import type {ReservationLocation} from '@wix/ambassador-table-reservations-v1-reservation-location/types'

import type {CancellationFeePreviewResponse} from '../services/cancellationFeeService'

import {isCancellationFeeApplied, isReservationCharged} from './cancellationFeeState'
import {hasPrepayment} from './cancellationPolicy'
import {hasPrepaidOrder as orderHasPrepayment} from './ecom'
import {getFormattedPrice} from './getFormattedPrice'
import {hasOrderCancellationFee, isUnchangedPrepaidOrder} from './orderPayment'
import {getSavedCardFromOrder, hasCapturedCreditCard} from './savedCard'

export const getFeeWarningKey = (isPerGuestFee: boolean, hasCard: boolean) => {
  if (isPerGuestFee && hasCard) {
    return 'uou-reservations.reservation-confirmation.cancel-fee-warning'
  }

  if (isPerGuestFee) {
    return 'uou-reservations.reservation-confirmation.cancel-fee-warning.no-card'
  }

  if (hasCard) {
    return 'uou-reservations.reservation-confirmation.cancel-fee-warning.flat'
  }

  return 'uou-reservations.reservation-confirmation.cancel-fee-warning.flat.no-card'
}

export const isFeeAlreadyApplied = (
  preview?: CancellationFeePreviewResponse,
  reservation?: Reservation,
  orderDetails?: Order | null,
) => {
  if (
    preview?.noFeeReason === 'ALREADY_APPLIED' ||
    preview?.cancellationFeeState?.cancellationFeeApplied
  ) {
    return true
  }

  if (
    preview?.collectionRoute === 'PREPAYMENT_KEEP_AND_REFUND' ||
    isUnchangedPrepaidOrder(orderDetails)
  ) {
    return hasOrderCancellationFee(orderDetails) || isCancellationFeeApplied(reservation)
  }

  return isReservationCharged(reservation)
}

export const formatFeeAmount = (
  amount?: string | null,
  currency?: string | null,
  locale?: string | null,
) => {
  if (amount === undefined || amount === null || amount === '') {
    return undefined
  }

  const numericAmount = Number(amount)

  if (!Number.isFinite(numericAmount) || !currency) {
    return amount
  }

  try {
    return getFormattedPrice(numericAmount, currency, locale)
  } catch {
    return amount
  }
}

export const getCancellationFeeWarning = ({
  reservation,
  reservationLocation,
  orderDetails,
  preview,
  locale,
}: {
  reservation?: Reservation
  reservationLocation?: ReservationLocation
  orderDetails?: Order | null
  preview?: CancellationFeePreviewResponse
  locale?: string | null
}) => {
  const feeAlreadyApplied = isFeeAlreadyApplied(preview, reservation, orderDetails)
  const feeApplies = !feeAlreadyApplied && preview?.applies === true
  const snapshotFee = reservation?.cancellationPolicy?.cancellationFee
  const locationFee = reservationLocation?.configuration?.cancellationPolicy?.cancellationFee
  const feeUnit =
    snapshotFee?.unit && snapshotFee.unit !== FeeUnit.UNKNOWN_FEE_UNIT
      ? snapshotFee.unit
      : locationFee?.unit
  const isPerGuestFee = feeUnit === FeeUnit.PER_GUEST
  const currency = preview?.currency ?? orderDetails?.currency ?? reservationLocation?.currency
  const policyAmount = snapshotFee?.amount ?? locationFee?.amount
  const partySize = reservation?.details?.partySize ?? 1
  const totalAmount = getTotalAmount(preview?.amount, policyAmount, partySize, isPerGuestFee)
  const formattedTotal = formatFeeAmount(totalAmount, currency, locale)
  const formattedPerGuest = isPerGuestFee
    ? formatFeeAmount(getPerGuestAmount(totalAmount, partySize, policyAmount), currency, locale)
    : undefined

  const savedCard = getSavedCardFromOrder(orderDetails)

  return {
    feeApplies,
    feeAlreadyApplied,
    hasCapturedCard:
      hasCapturedCreditCard(orderDetails) || preview?.collectionRoute === 'SAVED_PAYMENT_METHOD',
    hasPrepaidOrder:
      orderHasPrepayment(orderDetails) ||
      preview?.collectionRoute === 'PREPAYMENT_KEEP_AND_REFUND' ||
      hasPrepayment(reservationLocation),
    isPerGuestFee,
    formattedTotal,
    formattedPerGuest,
    currency,
    savedCard,
    guestPolicy:
      reservation?.cancellationPolicy?.guestPolicy ??
      reservationLocation?.configuration?.cancellationPolicy?.guestPolicy,
  }
}

export type CancellationFeeWarning = ReturnType<typeof getCancellationFeeWarning>

const getTotalAmount = (
  previewAmount?: string | null,
  policyAmount?: string | null,
  partySize?: number | null,
  isPerGuestFee?: boolean,
) => {
  const previewTotal = Number(previewAmount)

  if (previewAmount && Number.isFinite(previewTotal) && previewTotal > 0) {
    return previewAmount
  }

  if (!policyAmount) {
    return undefined
  }

  if (isPerGuestFee && partySize && partySize > 0) {
    const total = Number(policyAmount) * partySize

    return Number.isFinite(total) ? String(total) : policyAmount
  }

  return policyAmount
}

const getPerGuestAmount = (
  totalAmount?: string | null,
  partySize?: number | null,
  feeAmount?: string | null,
) => {
  if (totalAmount && partySize && partySize > 0) {
    const perGuest = Number(totalAmount) / partySize

    if (Number.isFinite(perGuest)) {
      return String(perGuest)
    }
  }

  return feeAmount
}
